import React from "react";
import {
  FormControl,
  TextField,
  FormHelperText,
  Popper,
  Autocomplete,
  createFilterOptions,
} from "@mui/material";
import styled from "styled-components";
import { FieldProps, useFormFull } from "form-full";
import { FontStyles } from "../index";
import { fonts } from "../../config";

export type TextInputAutoCompleteBaseProps = {
  label?: string;
  hideVisualError?: boolean;
  smallBottom?: number;
  alternativeColors?: boolean;
  heightInput?: number;
  inputStyle?: any;
  disabled?: boolean;
  required?: string;
  readOnly?: boolean;
  placeholder?: string;
  justNumbers?: boolean;
  options: Array<{
    label: string;
    value: string | number;
  }>;
  name: string;
};

const StyledInput = styled(TextField)<{
  $inputStyle?: any;
  $error?: boolean;
  $smallBottom?: number;
  $alternativecolors?: boolean;
  $height?: number;
}>(
  ({
    theme,
    $inputStyle,
    $error,
    $smallBottom,
    $alternativecolors,
    $height,
  }) => {
    const { spacing, palette: colors } = theme as any;
    return {
      paddingBlock: spacing(1),
      paddingTop: $smallBottom === 2 ? spacing(1) : null,
      paddingInline: spacing(1),
      textOverflow: "ellipsis",
      backgroundColor: colors.primary.contrastText,
      borderRadius: spacing(0.625),
      height: $height === 2 ? spacing(6) : null,
      boxShadow: $error
        ? `inset 0 0 0 2px ${colors.text.secondary}`
        : `inset 0 0 0 ${colors.error.main}`,

      "& .MuiInput-input": {
        color: $alternativecolors ? colors.text.secondary : colors.text.primary,
        fontFamily: fonts.medium,
        fontSize: fonts.sizes.regular,

        "&::placeholder": {
          opacity: 0.8,
        },
      },
      "& .MuiIconButton-root": {
        color: colors.text.primary,
      },
      ...$inputStyle,
    };
  }
);

const Label = styled.p<{ $witherror?: boolean; disabled?: boolean }>(({ theme, $witherror, disabled }) => {
  const { palette: colors } = theme as any;
  return {
    ...FontStyles.semibold14,
    textTransform: "uppercase",
    position: "relative",
    top: 0,
    left: 0,
    color: $witherror ? colors.error.main : colors.action.selected,
    transition: ".2s",
    pointerEvents: "none",
    opacity: disabled ? 0.6 : 1,
  };
});

const ErrorMessage = styled(FormHelperText)(({ theme }) => {
  const { palette: colors } = theme as any;
  return {
    color: colors.text.secondary,
  };
});

const PopperSelect = styled(Popper)(({ theme }) => {
  const { palette: colors } = theme as any;
  return {
    "& .MuiAutocomplete-listbox": {
      color: colors.text.primary,
      backgroundColor: colors.primary.contrastText,
      fontFamily: fonts.medium,
      fontSize: fonts.sizes.regular,
      "& li:hover": { backgroundColor: colors.action.focus + 20 },
      '& [aria-selected="true"]': {
        backgroundColor: colors.primary.contrastText + 40,
        borderColor: "transparent",
      },
      "::-webkit-scrollbar-thumb": {
        background: colors.primary.contrastText,
        borderRadius: "15px",
        height: "2px",
      },
    },
  };
});

const StyledAutocomplete = styled(Autocomplete)(({ theme }) => {
  const { spacing } = theme as any;
  return {
    '& .MuiAutocomplete-inputRoot[class*="MuiInput-root"]': {
      paddingBlock: spacing(1.0625),
      paddingInline: spacing(2.5),
      margin: 0,
      "&::before": { display: "none" },
      "&::after": { display: "none" },
    },
  };
}) as typeof Autocomplete;

function InputComponent(props: TextInputAutoCompleteBaseProps) {
  const { value, error, onBlur, ref, onChange, ffHandler } =
    useFormFull.field(props);

  const {
    label,
    hideVisualError,
    smallBottom,
    alternativeColors,
    heightInput,
    options,
    inputStyle,
    disabled,
    required,
    readOnly,
    placeholder = "Todos",
    justNumbers = false,
  } = props;

  let focus = false;

  React.useEffect(() => {
    if (ref && value === "") {
      const ele = (ref as any).current?.getElementsByClassName(
        "MuiAutocomplete-clearIndicator"
      )[0];
      if (ele) {
        ele.click();
      }
    }
  }, [value, ref]);

  const filterOptions = createFilterOptions({
    stringify: (option: any) => option.label?.replace(/[.,/\\-]/g, ""),
  });

  const filterOptionsDefault = createFilterOptions({
    stringify: (option: any) => option.label,
  });

  return (
    <FormControl fullWidth error={hideVisualError ? undefined : Boolean(error)}>
      <StyledAutocomplete
        filterOptions={justNumbers ? filterOptions : filterOptionsDefault}
        ref={ref}
        key={label}
        options={options ? [{ label: "Todos", value: -1 }, ...options] : []}
        slots={{
          popper: PopperSelect as any,
        }}
        clearOnBlur={false}
        clearOnEscape={false}
        disabled={readOnly || disabled}
        getOptionLabel={(option: any) => option.label || ""}
        onChange={(event, value: any) => {
          if (value === null || value === -1) {
            onChange(event, null);
          } else {
            onChange(event, value.value);
          }
          setTimeout(() => {
            onBlur(ffHandler?.testFieldError(props.name));
          }, 10);
        }}
        renderInput={(params) => (
          <>
            <Label
              disabled={disabled}
              $witherror={Boolean(error) ? true : undefined}
            >
              {label}
            </Label>
            <StyledInput
              placeholder={placeholder}
              value={value}
              onFocus={() => (focus = true)}
              required={!!required}
              $inputStyle={inputStyle}
              $alternativecolors={alternativeColors}
              $height={heightInput}
              $smallBottom={smallBottom}
              $error={Boolean(error)}
              error={Boolean(error)}
              {...params}
            />
          </>
        )}
      />

      <ErrorMessage error={Boolean(error)}>
        {Boolean(error) ? error : " "}
      </ErrorMessage>
    </FormControl>
  );
}

export default InputComponent;
